import { TherapistPaginationSearchResponseDto } from '@app/api';
import { useClientSize } from '@app/hooks';
import { useInfiniteScroll } from '@app/pageComponents/AllTherapistsPage/useInfiniteScroll';
import { BreakpointsEnum } from '@app/types';
import LoadingState from '@app/ui/components/LoadingState';
import SpecialistCard from '@app/ui/components/SpecialistCard';
import { forwardRef, useImperativeHandle } from 'react';
import styled, { css } from 'styled-components';
import { TypeCard } from '../../AllTherapists';

interface Props {
  therapists?: TherapistPaginationSearchResponseDto;
  typeCard: TypeCard;
  onReachEnd?: () => void;
  isLoading?: boolean;
}

export interface ImperativeHandleProps {
  reset: () => void;
}

const List = forwardRef<ImperativeHandleProps, Props>(
  ({ therapists, typeCard, onReachEnd, isLoading }, ref) => {
    const isEnd = therapists && therapists.page >= therapists.totalPage;
    const { loadMoreRef, loadedTherapists, reset } =
      useInfiniteScroll<HTMLDivElement>({
        onIntersect: (entry) => {
          if (entry.isIntersecting && !isEnd) {
            onReachEnd?.();
          }
        },
        therapists,
        freeze: isEnd,
      });

    useImperativeHandle(ref, () => ({ reset }), []);

    const { getIsBreakpoint } = useClientSize();

    if (!therapists) return null;

    const isMobile = getIsBreakpoint('sm');

    const isRow = !isMobile && typeCard === TypeCard.original;
    const isSmall = !isMobile && typeCard === TypeCard.small;

    const dataToShow = loadedTherapists.data.length
      ? loadedTherapists
      : therapists;

    return (
      <>
        <TherapistsList $isRow={isRow}>
          {dataToShow?.data?.map((item) => {
            return (
              <SpecialistCard key={item.id} data={item} isSmall={isSmall} />
            );
          })}
        </TherapistsList>
        <Footer ref={loadMoreRef}>
          {!isEnd && isLoading && <LoadingState />}
        </Footer>
      </>
    );
  },
);

List.displayName = 'List';

export default List;

const TherapistsList = styled.div<{ $isRow: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 50px 19px;
  margin-top: 50px;

  @media (max-width: ${BreakpointsEnum.md}px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  ${({ $isRow }) => {
    if ($isRow)
      return css`
        grid-template-columns: 1fr;
        gap: 20px;
        @media (max-width: ${BreakpointsEnum.md}px) {
          grid-template-columns: 1fr;
          gap: 20px;
        }
      `;
  }};

  @media (max-width: ${BreakpointsEnum.sm}px) {
    grid-template-columns: 1fr;
    gap: 80px;
  }
`;

const Footer = styled.footer`
  padding-top: 30px;
  padding-bottom: 30px;
`;
